/**
 * The console's idea of "who am I, which tenant, which agent".
 *
 * `GET /me` is fetched once and shared. The selected agent lives here too,
 * because almost every screen is scoped to one agent and the switcher must
 * survive a page reload.
 *
 * Capability checks go through `personCan` from `@otto-iq/shared`, so the nav
 * and the API agree on what a viewer may see by construction rather than by
 * two lists that drift.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { personCan, type Capability, type PersonPrincipal, type Role } from '@otto-iq/shared';
import {
  api,
  getOperatorToken,
  type AgentDTO,
  type MeResponse,
  type OperatorContext,
  type TenantDTO,
} from './api';
import { consoleSurface } from './auth';
import { queryKeys } from './query';

const ACTIVE_AGENT_STORAGE_KEY = 'otto-iq.active-agent';

function readStoredAgentId(): string | null {
  try {
    return globalThis.localStorage?.getItem(ACTIVE_AGENT_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeStoredAgentId(agentId: string | null): void {
  try {
    if (agentId) globalThis.localStorage?.setItem(ACTIVE_AGENT_STORAGE_KEY, agentId);
    else globalThis.localStorage?.removeItem(ACTIVE_AGENT_STORAGE_KEY);
  } catch {
    /* Private-mode browsers: the switcher just forgets between reloads. */
  }
}

export interface ConsoleSession {
  me: MeResponse | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;

  person: MeResponse['person'] | undefined;
  role: Role;
  tenant: TenantDTO | null;
  operator: OperatorContext | null;
  isOttoStaff: boolean;

  agents: AgentDTO[];
  agentsLoading: boolean;
  agentsError: unknown;
  activeAgent: AgentDTO | null;
  activeAgentId: string | null;
  setActiveAgentId: (agentId: string) => void;

  /** Role + status + per-person flags, exactly as the API will evaluate it. */
  can: (capability: Capability) => boolean;
  principal: PersonPrincipal;
}

const SessionContext = createContext<ConsoleSession | null>(null);

export function ConsoleSessionProvider({ children }: { children: ReactNode }) {
  const meQuery = useQuery({
    queryKey: queryKeys.me(),
    queryFn: ({ signal }) => api.me(signal),
    staleTime: 60_000,
  });

  // `GET /agents` is a client-surface route, so on the master build it is a
  // guaranteed 403: that build holds a master-surface token and has no tenant
  // scope. Firing it anyway filled the master console's network log with
  // failures that looked like a broken console rather than a boundary working.
  // Operator mode is the exception — it swaps in a token that does name a
  // tenant, and the client shell it renders needs the agent list.
  const agentsUsable = consoleSurface !== 'master' || getOperatorToken() !== null;

  const agentsQuery = useQuery({
    queryKey: queryKeys.agents(),
    queryFn: ({ signal }) => api.listAgents(signal),
    enabled: meQuery.isSuccess && agentsUsable,
  });

  const [storedAgentId, setStoredAgentId] = useState<string | null>(() => readStoredAgentId());

  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);

  const activeAgent = useMemo(() => {
    if (agents.length === 0) return null;
    return agents.find((agent) => agent.id === storedAgentId) ?? agents[0] ?? null;
  }, [agents, storedAgentId]);

  // Keep the stored id honest: a suspended or deleted agent must not pin the UI.
  useEffect(() => {
    if (activeAgent && activeAgent.id !== storedAgentId) {
      setStoredAgentId(activeAgent.id);
      writeStoredAgentId(activeAgent.id);
    }
  }, [activeAgent, storedAgentId]);

  const setActiveAgentId = useCallback((agentId: string) => {
    setStoredAgentId(agentId);
    writeStoredAgentId(agentId);
  }, []);

  const me = meQuery.data;
  const role: Role = me?.role ?? me?.person?.role ?? 'viewer';

  // `GET /me` (apps/api/src/routes/me.ts) actually answers with
  // `operatorFor: tenantId | null`, not the `operator: OperatorContext` object
  // this DTO declares — the same species of client/API drift as the
  // decideSkill mismatch in api.ts. Read the real field, falling back to a
  // synthesized OperatorContext (the operator's own name/email are already
  // what `person` holds while operating — see `operatorContext()` in
  // apps/api/src/auth/plugin.ts) so the banner still lights up. The `operator`
  // shape is read first so fixtures/tests built against the documented DTO
  // keep working unchanged.
  const wireOperatorFor = (me as unknown as { operatorFor?: string | null } | undefined)
    ?.operatorFor;
  const declaredOperator: OperatorContext | null =
    me?.operator ??
    (wireOperatorFor
      ? {
          active: true,
          operatorName: me?.person?.name,
          operatorEmail: me?.person?.email,
          expiresAt: getOperatorToken()?.expiresAt,
        }
      : null);
  const operator = declaredOperator && declaredOperator.active ? declaredOperator : null;

  const principal: PersonPrincipal = useMemo(
    () => ({
      id: me?.person?.id,
      role,
      canApprove: me?.person?.canApprove,
      canTeach: me?.person?.canTeach,
      status: me?.person?.status,
      approvalLimitCents: me?.person?.approvalLimitCents,
    }),
    [me, role],
  );

  const value = useMemo<ConsoleSession>(
    () => ({
      me,
      isLoading: meQuery.isLoading,
      error: meQuery.error,
      refetch: () => void meQuery.refetch(),

      person: me?.person,
      role,
      tenant: me?.tenant ?? null,
      operator,
      isOttoStaff: me?.isOttoStaff === true,

      agents,
      // Not just `agentsQuery.isLoading`: that TanStack v5 flag is
      // `isPending && isFetching`, and `agentsQuery` sits disabled (fetchStatus
      // 'idle', so `isFetching` is false) until `meQuery` succeeds — so it
      // reads `false` during that window even though we do not yet know the
      // real agent. Every consumer gating a render on "do we know the agent
      // yet" (agent-settings.tsx's own loading branch, and the agentId-keyed
      // queries in skills.tsx/integrations.tsx) briefly saw `agentsLoading:
      // false` with `agents: []` before flipping to the real state — a
      // reproducible flash of "no agent"/unscoped data on every page load.
      agentsLoading: !meQuery.isSuccess || agentsQuery.isLoading,
      agentsError: agentsQuery.error,
      activeAgent,
      activeAgentId: activeAgent?.id ?? null,
      setActiveAgentId,

      // `isError`, deliberately, and not `!isSuccess`: when `GET /me` fails,
      // `me` is undefined, `role` falls back to 'viewer' above, and
      // `principal.status` is undefined — which `isActivePerson` in
      // `@otto-iq/shared` reads as *active*, because on the server a principal
      // always comes from a real row. So an unrecognised account held every
      // viewer capability and the sidebar rendered for someone the API refuses
      // outright. `SessionGate` is what actually stops that; this makes a
      // regression there fail closed instead of restoring a browsable nav.
      //
      // Gating on `isSuccess` would also cover it, and is wrong: it reads false
      // while the request is still in flight, so every capability-gated screen
      // renders its "you may not" refusal for a beat before flipping to real
      // content. Same flash-of-wrong-state the `agentsLoading` note above
      // describes. In flight we stay optimistic; only a definite failure closes.
      can: (capability: Capability) => !meQuery.isError && personCan(principal, capability),
      principal,
    }),
    [
      me,
      meQuery.isLoading,
      meQuery.isSuccess,
      meQuery.isError,
      meQuery.error,
      meQuery.refetch,
      role,
      operator,
      agents,
      agentsQuery.isLoading,
      agentsQuery.error,
      activeAgent,
      setActiveAgentId,
      principal,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): ConsoleSession {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside a <ConsoleSessionProvider>.');
  return context;
}

/** Convenience for the many screens that are meaningless without an agent. */
export function useActiveAgent(): AgentDTO | null {
  return useSession().activeAgent;
}
